/* 
Made by: Miska Nevalainen 2022-2023, as part of traineeship
This tool is meant to be a part of a big website (Forest biomarkets gateway) to guide and inform users about different aspects of Forest Bioeconomy

Tree.js
Main file of the App. Uses the other components to render an interactive hierarchical node tree filled with data. 
Also houses the data for the displaydata window (when the user clicks on a tree node to find information about a certain topic)

*/

import React from "react";
import { Group } from "@vx/group";
import { Tree } from "@vx/hierarchy";
import { LinearGradient } from "@vx/gradient";
import { hierarchy } from "d3-hierarchy";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";
import "./Tree.css";
import Forestmodel from "./assets/svgs/FEFRforestbioeconomy.svg";
import TreeData from "./data/TreeData";
import Circle from "./assets/svgs/Circle";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
  AccordionItemState,
} from "react-accessible-accordion";
import "react-accessible-accordion/dist/fancy-example.css"; // This will add some basic styling

// import Links from './Links';
import Links from "./LinksMove";

// import Nodes from './Nodes';
import Nodes from "./NodesMove";

export default class extends React.Component {
  state = {
    layout: "polar",
    orientation: "vertical",
    linkType: "curve",
    stepPercent: 1,
    displayedData: "",
    showData: false,
    counter: 0,
    circleVisible: true, // Circular circle visibility
    customTranslateX: 415,
    customTranslateY: -265,
    customScale: 5.2,
    openAccordionItem: null,
  };

  render() {
    const { customTranslateX, customTranslateY, customScale } = this.state;
    const {
      data,
      width,
      height,
      margin = {
        top: 120,
        left: 30,
        right: 30,
        bottom: 120,
      },
    } = this.props;
    const { layout, orientation, linkType, stepPercent, circleVisible } =
      this.state;

    if (width < 10) return null;

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    let origin;
    let sizeWidth;
    let sizeHeight;

    if (layout === "polar") {
      origin = {
        x: innerWidth / 2,
        y: innerHeight / 2,
      };
      sizeWidth = 2 * Math.PI;
      sizeHeight = Math.min(innerWidth, innerHeight) / 2;
    } else {
      origin = { x: 0, y: 0 };
      if (orientation === "vertical") {
        sizeWidth = innerWidth;
        sizeHeight = innerHeight;
      } else {
        sizeWidth = innerHeight;
        sizeHeight = innerWidth;
      }
    }

    const root = hierarchy(data, (d) => (d.isExpanded ? d.children : null));

    const openDataContainer = () => {
      const container = document.querySelector(".dataDisplayContainer");
      container.classList.add("open"); // Add the open class to the container to slide it open
    };

    const closeDataContainer = () => {
      const container = document.querySelector(".dataDisplayContainer");
      container.classList.remove("open"); // Remove the 'open' class to slide the container closed
    };

    const handleAccordionClick = (isExpanded) =>{
      
    }

    

    /* Handles the changing of data in the displaydata window */
    const changeData = (name) => {
      openDataContainer();


      const { counter } = this.state;
      const newCounter = counter + 1;
      this.setState({ counter: newCounter });

      this.displayedData = (
        <div
          className={`displayData slide-down-${newCounter}`}
          key={newCounter}
        >
          <button
            className="displayData_close-button"
            onClick={closeDataContainer}
          >
            X
          </button>
          <Accordion allowZeroExpanded>
            {TreeData[name].map((category, index) => {
              // Sort items alphabetically by name before mapping
              const sortedItems = [...category.items].sort((a, b) =>
                a.name.localeCompare(b.name)
              );

              return (
                <div key={index}>
                  <h6 className="displayData_title">{category.title}</h6>
                  {sortedItems.map((item, itemIndex) => (
                    <AccordionItem key={itemIndex}>
                      <AccordionItemHeading onClick={(e) => {
                      const isExpanded = e.currentTarget.getAttribute('aria-expanded') === true;
                      handleAccordionClick(isExpanded);
                    }}>
                        <AccordionItemButton className="listItem">
                          {item.name === "" ? "-" : item.name}{" "}
                          {item.data && (
                           <AccordionItemState>
                           {({ expanded }) => (expanded ? <i className="fas fa-chevron-down"></i> : <i className="fas fa-chevron-up"></i>)}
                            </AccordionItemState>
                          )}
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        {item.subItems ? (
                          <div>
                            {item.data && item.data !== "." && (
                              <h6 className="displayData_title">{item.data}</h6>
                            )}
                            <Accordion allowZeroExpanded>
                              {item.subItems
                                .sort((a, b) => a.name.localeCompare(b.name)) // Sorting subItems
                                .map((subItem, subItemIndex) => (
                                  <AccordionItem key={subItemIndex}>
                                    <AccordionItemHeading>
                                      <AccordionItemButton className="listItem">
                                        {subItem.name}{" "}
                                        {subItem.data && (
                                          <span className="accordion-arrow">
                                            <i className="fas fa-chevron-up"></i>
                                          </span>
                                        )}
                                      </AccordionItemButton>
                                    </AccordionItemHeading>

                                    <AccordionItemPanel>
                                      <p className="displayData_data">{subItem.data}</p>
                                      {subItem.link && (
                                        <p>
                                          <a
                                            className="dataDisplay_link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Read more
                                          </a>
                                        </p>
                                      )}
                                    </AccordionItemPanel>
                                  </AccordionItem>
                                ))}
                            </Accordion>
                          </div>
                        ) : (
                          <div>
                            <p className="displayData_data">{item.data}</p>
                            {item.link && (
                              <p className="displayData_data">
                                <a
                                  className="dataDisplay_link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Read more
                                </a>
                              </p>
                            )}
                          </div>
                        )}
                      </AccordionItemPanel>
                    </AccordionItem>
                  ))}
                </div>
              );
            })}
          </Accordion>
        </div>
      );
    };

    /* Handles the circular click outside of the switch case structure below */
    const handleCircleClick = () => {
      changeData("Circular");
      this.setState({ selectedNode: "Circular" });
    };

    /*  Switch case to check the clicked node and display node specific information*/
    const populateDisplay = (node) => {
      switch (node.data.name) {
        case "Supporting services": {
          this.setState({ selectedNode: "Supporting services" });
          changeData("Supporting services", node);
          break;
        }
        case "Regulating services": {
          this.setState({ selectedNode: "Regulating services" });
          changeData("Regulating services", node);
          break;
        }
        case "Provisioning services": {
          this.setState({ selectedNode: "Provisioning services" });
          changeData("Provisioning services", node);
          break;
        }
        case "Cultural services": {
          this.setState({ selectedNode: "Cultural services" });
          changeData("Cultural services", node);
          break;
        }
        case "Natural Capital": {
          this.setState({ selectedNode: "Natural capital" });
          changeData("Natural Capital", node);
          break;
        }
        case "Bioproducts": {
          this.setState({ selectedNode: "Bioproducts" });
          changeData("Bioproducts", node);
          break;
        }
        case "Wood based products": {
          this.setState({ selectedNode: "Wood based products" });
          changeData("Wood based products", node);
          break;
        }
        case "Non-wood products": {
          this.setState({ selectedNode: "Non-wood products" });
          changeData("Non-wood products", node);
          break;
        }
        case "Sustainable businesses": {
          this.setState({ selectedNode: "Sustainable businesses" });
          changeData("Sustainable businesses", node);
          break;
        }
        case "Sustainable finance": {
          this.setState({ selectedNode: "Sustainable finance" });
          changeData("Sustainable finance", node);
          break;
        }
        case "Technologies/Innovations": {
          this.setState({ selectedNode: "Technologies / Innovations" });
          changeData("Technologies / Innovations", node);
          break;
        }
        case "Biocities": {
          this.setState({ selectedNode: "Biocities" });
          changeData("Biocities", node);

          break;
        }
        case "Services": {
          this.setState({ selectedNode: "Services"});
          changeData("Services", node);
          
          break;
        }
        case "Bioenergy": {
          this.setState({ selectedNode: "Bioenergy" });
          changeData("Bioenergy", node);

          break;
        }
        case "Social capital": {
          this.setState({ selectedNode: "Social capital" });
          changeData("Social capital", node);

          break;
        }
        case "Careers": {
          this.setState({ selectedNode: "Careers" });
          changeData("Careers", node);

          break;
        }
        case "Education": {
          this.setState({ selectedNode: "Education" });
          changeData("Education", node);

          break;
        }
        case "Qualification": {
          this.setState({ selectedNode: "Qualification" });
          changeData("Qualification", node);

          break;
        }
        case "Green jobs": {
          this.setState({ selectedNode: "Green jobs" });
          changeData("Green jobs", node);

          break;
        }

        case "Gender aspects": {
          this.setState({ selectedNode: "Gender aspects" });
          changeData("Gender aspects", node);

          break;
        }

        case "Prosperity": {
          this.setState({ selectedNode: "Prosperity" });
          changeData("Prosperity", node);

          break;
        }
        case "Wellbeing": {
          this.setState({ selectedNode: "Wellbeing" });
          changeData("Wellbeing", node);
          break;
        }

        case "Circular": {
          this.setState({ selectedNode: "Circular" });
          changeData("Circular", node);

          break;
        }

        default: {
          console.error(
            "Invalid node data name: " +
              node.data.name +
              ". This might be due to there not being any child data within this node"
          );
          break;
        }

        // Parent node cases, reset the data of the display window
      }
    };

    return (
      <div className="dataDisplay">
        <div className="dataDisplayContainer">
          <image src="" />
          <div className="dataDisplayItemsContainer">
            <div>
              <p className="dataDisplayNodeName">{this.state.selectedNode}</p>
            </div>

            {/* Display the clicked node's name here */}
            {this.displayedData}
          </div>
        </div>

        <svg
          viewBox={`0 -100 ${width} ${height + 270}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <path
              id="circularPath"
              d={`
               M ${width / 2 - 150}, ${height / 2 - 250} 
               a 500, 420 0 1,0 300,0  {/* Adjusted the path here */}
                `}
            />
          </defs>
          <defs>
            <filter id="glow">
              <feGaussianBlur
                in="SourceGraphic"
                stdDeviation="5"
                result="blur"
              />
              <feFlood floodColor="your-glow-color" result="glowColor" />
              <feComposite
                in="glowColor"
                in2="blur"
                operator="in"
                result="softGlow_colored"
              />
              <feMerge>
                <feMergeNode in="softGlow_colored" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <defs>
            <linearGradient id="yellowToGreen" x1="0.5" y1="0" x2="0.5" y2="1">
              <stop offset="55%" stopColor="#f6e05e" />
              <stop offset="100%" stopColor="#adffff" />
              <stop offset="100%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#377a7b" />
              <stop offset="100%" stopColor="#c05621" />
              <stop offset="100%" stopColor="#e53e3e" />
            </linearGradient>
            <linearGradient id="blueToGreen" x1="0.5" y1="1" x2="0.5" y2="0">
              <stop offset="55%" stopColor="#175659" />
              <stop offset="100%" stopColor="#adffff" />
              <stop offset="100%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#377a7b" />
              <stop offset="100%" stopColor="#c05621" />
              <stop offset="100%" stopColor="#e53e3e" />
            </linearGradient>
          </defs>
          {circleVisible && (
            <g>
              <circle
                className="circularCircle"
                onClick={handleCircleClick}
                cx={width / 2}
                cy={height / 2}
                r={Math.min(width, height + 100) / 2}
                fill="transparent"
                stroke="transparent"
                strokeWidth="1"
              />

              <text
                id="circularText"
                x={width / 2}
                y={height / 2 - 400}
                textAnchor="middle"
                fontSize="26"
                fill="black"
              ></text>
            </g>
          )}

          {/* Custom SVG Group with Transforms */}

          {/* Circular container with nature and renewable */}
          <g
            transform={`translate(${customTranslateX}, ${customTranslateY}) scale(${customScale})`}
          >
            {/* Circle */}
            <Circle />
          </g>

          {/*  {/* Leaf */}
          {/* <g transform="translate(1070,935)">
            <Leaf additionalStyles={{ fill: "black" }} />
          </g> */}
          {/* Leaf */}
          {/* <g transform="translate(1120,910)">
            <Leaf additionalStyles={{ fill: "black" }} />
          </g> */}
          {/* Leaf */}
          {/* <g transform="translate(1020,950)">
            <Leaf additionalStyles={{ fill: "black" }} />
          </g> */}
          {/* Leaf */}
          {/* <g transform="translate(870,950)">
            <Leaf
              additionalStyles={{ fill: "black", transform: "scaleX(-1)" }}
            />
          </g> */}
          {/* Leaf */}
          {/* <g transform="translate(820,935)">
            <Leaf
              additionalStyles={{ fill: "black", transform: "scaleX(-1)" }}
            />
          </g> */}
          {/* Leaf */}
          {/* <g transform="translate(770,910)">
            <Leaf
              additionalStyles={{ fill: "black", transform: "scaleX(-1)" }}
            />
          </g> */}
          {/* Sun SVG */}
          {/* <g transform="translate(1200, -85) scale(0.4)">
            <Sun additionalStyles={{}} />
          </g>
          {/* SVG TEXT */}

          {/* <text id="circularText">
            <textPath
              href="#circularPath" // Reference to the path's ID
              startOffset="50%" // Centers the text along the path
              textAnchor="middle" // Centers the text based on its position
              dominantBaseline="middle"
            >
              Circularity
            </textPath>
          </text> */}
          {/* <text
            className="svgTextElementHeading"
            id="circularText"
            x={width / 2}
            y={height / 2 - 490}
            textAnchor="middle"
            fontSize="26"
            fill="black"
          >
            Renewable Energy
          </text>
          <text
            className="svgTextElementHeading"
            id="circularText"
            x={width / 2}
            y={height / 2 + 505}
            textAnchor="middle"
            fontSize="26"
            fill="white"
          >
            Nature
          </text> */}

          <defs>
            {/* Define a pattern with an image */}
            <pattern
              id="circle-pattern"
              patternUnits="objectBoundingBox"
              width="1"
              height="1"
            >
              {/* Replace 'circle-image.jpg' with the path to your image */}
              <image href={Forestmodel} x="0" y="0" width="200" height="200" />
            </pattern>
          </defs>

          <LinearGradient id="lg" from="#fd9b93" to="#fe6e9e" />

          <LinearGradient
            id="nodeActiveColor"
            from="#5f4037"
            to="#99f2c8"
            gradientTransform="rotate(60)"
          />

          <Tree
            top={margin.top}
            left={margin.left}
            root={root}
            size={[sizeWidth, sizeHeight]}
            separation={(a, b) => {
              if (a.depth === 1 && b.depth === 1) {
                return 3; // Increase the spacing between outermost children nodes
              } else if (a.depth === 2 && b.depth === 2) {
                return 0.6; // Increase the spacing between the pathways
              } else {
                return (
                  (a.children === b.children && a.parent === b.parent
                    ? 1
                    : 1.5) / a.depth
                );
              }
            }}
          >
            {({ data }) => (
              <Group top={origin.y} left={origin.x}>
                <Links
                  links={data.links()}
                  linkType={linkType}
                  layout={layout}
                  orientation={orientation}
                  stepPercent={stepPercent}
                />

                <Nodes
                  nodes={data.descendants()}
                  layout={layout}
                  orientation={orientation}
                  selectedNode={this.state.selectedNode}
                  onNodeClick={(node) => {
                    if (this.state.selectedNode === node.data.name) {
                      this.setState({ selectedNode: null });
                    } else {
                      this.setState({ selectedNode: node.data.name });
                    }

                    this.setState({ circleVisible: true });

                    if (!node.data.children) {
                      populateDisplay(node);
                    }

                    if (node.data.children) {
                      closeDataContainer();
                    }

                    if (!node.data.isExpanded) {
                      node.data.x0 = node.x;
                      node.data.y0 = node.y;
                    }
                    node.data.isExpanded = !node.data.isExpanded;
                    this.forceUpdate();
                  }}
                />
              </Group>
            )}
          </Tree>
        </svg>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Tree.js